import {
    forwardRef,
    ReactElement,
    SetStateAction,
    Suspense,
    useCallback,
    useLayoutEffect,
    useMemo,
    useState,
} from 'react';
import {
    Button,
    FilterChip,
    FilterExpressionSelect,
    FilterItem,
    FilterItemProps,
    FilterValueChip,
    IconArrowDown,
    IconArrowUp,
    IconRefresh,
    ListInputItem,
    ListItem,
    ListSection,
    MultiSelectProps,
    Select,
    SelectTriggerProps,
    TestProps,
    Text,
    useFilterExpressionSelect,
    VStack,
} from '@direct-frontend/components';
import {
    BaseRowData,
    createLoadingDataProvider,
    createStaticDataProvider,
    DataProvider,
} from '@adv-frontend/data-provider';
import { useAsyncFlow } from '@adv-frontend/modules-flow';
import { castRef, difference, DistributiveOmit, genericMemo, keys, noop, useDebounce } from '@direct-frontend/stdlib';
import { i18n } from './FilterSelectList.i18n';
import { OperatorType } from '../../lib/types/Operator';
import { getOperatorTitle } from '../../lib/helpers/getOperatorTitle';
import type { FilterSuggestProps, SelectWithSuggestFilterProps } from '../../lib/types/Filters';
import { ErrorBoundary, ErrorBoundaryProps } from '../../lib/helpers/ErrorBoundary';
import styles from './FilterSelectList.module.css';
import { ShowSelectedOnlySwitcher } from '../../components/ShowSelectedOnlySwitcher';

export type FilterSelectListProps<RowData extends BaseRowData = BaseRowData> = Pick<
    FilterItemProps,
    'size' | 'color' | 'onRemove'
> &
    DistributiveOmit<MultiSelectProps<string, string, RowData>, 'size' | 'color' | 'items' | 'onChange' | 'value'> &
    Pick<SelectWithSuggestFilterProps<RowData>, 'operator' | 'operators' | 'operatorTitles'> &
    FilterSuggestProps<RowData> & {
        /** Кол-во значений после которых сворачиваем фильтр*/
        shownValuesLimit?: number;
        /** Флаг отображения переключателя между отображением всех и только выбранных значений */
        showSelectedOnlySwitcher?: boolean;
        /** Функция рендера текста для свернутого фильтра. Например, `И еще ${restCount} Кампаний`*/
        renderValuesLimitText?: (restCount: number) => string;
        /** Обработчик изменения значений */
        onChange: (value: string[]) => void;
        /** Обработчик изменения оператора */
        onChangeOperator: (operator: OperatorType) => void;
        value: string[];
        /** Ошибки фильтра */
        errors?: string[];
        /** Обязательный фильтр */
        isRequired?: boolean;
        /** Элемент, который будет отрисован слева от title в триггере фильтра */
        addonLeft?: React.ReactNode;
    };

const defaultUseTitleByValue = <ValueType extends string>(value: ValueType[]) => {
    return value.map(value => ({ value, title: value }));
};

type ItemsTitlesCache = Record<string, ReactElement | string>;

const mapItemTitles = <RowData extends BaseRowData = BaseRowData>(
    items: (RowData | undefined)[],
    search: string,
    renderFilterChipsItem: (params: { item: RowData; search: string; props: { id: string } }) => ReactElement | string,
    prevState?: ItemsTitlesCache
) => {
    return items.reduce((acc, item) => {
        if (item) {
            acc[item.id] = renderFilterChipsItem({ item, search, props: { id: item.id } });
        }
        return acc;
    }, prevState ?? {});
};

const emptyArray: never[] = [];

/** Количество строк-стабов, отображаемых во время загрузки саджеста */
const LOADING_ROWS_COUNT = 5;

/**
 * Компонент для отображения фильтра с мультивыбором и cаджестом
 * Выбранные опции выстраиваются справа от названия фильтра в виде чипс
 */
export const FilterSelectList = genericMemo(function FilterSelectList<RowData extends BaseRowData = BaseRowData>(
    props: FilterSelectListProps<RowData>
) {
    const {
        className,
        'data-testid': dataTestId = 'FilterSelectListWithSuggest',
        color = 'normal',
        onRemove,
        onChange,
        onChangeOperator,
        dataProvider: DataProviderComponent,
        useTitleByValue = defaultUseTitleByValue,
        operator,
        operators,
        operatorTitles,
        renderSelectListItem,
        renderFilterChipsItem,
        shownValuesLimit,
        renderValuesLimitText,
        suggestPlaceholder,
        value,
        isDisabled,
        isRequired,
        addonLeft,
        ...restProps
    } = props;

    // Кэш имен элементов
    const [itemTitles, setItemTitles] = useState<ItemsTitlesCache>({});
    const [search, setSearch] = useState('');
    const queryArg = useMemo(() => {
        return {
            searchValue: search,
        };
    }, [search]);

    const clearFilter = useCallback(() => {
        onChange([]);
    }, [onChange]);

    // Рендер селектора в различных состояниях
    const loadingDataProvider = useMemo(() => createLoadingDataProvider<never>(LOADING_ROWS_COUNT), []);
    const createSelect = useSelectFactory({ ...props, setItemTitles, search, setSearch, itemTitles });
    const fallback = useMemo(() => createSelect(loadingDataProvider), [createSelect, loadingDataProvider]);

    const emptyDataProvider = useMemo(() => createStaticDataProvider(emptyArray), []);
    const errorFallback = useCallback<ErrorBoundaryProps['errorFallback']>(
        ({ error, onReset }) => createSelect(emptyDataProvider, { retry: onReset, error: Boolean(error) }),
        [createSelect, emptyDataProvider]
    );

    const selectRenderer = useCallback(
        (dataProvider: DataProvider<RowData>, allIds?: string[]) => createSelect(dataProvider, { allIds }),
        [createSelect]
    );

    // Параметры селектора оператора
    const operatorProps = useFilterExpressionSelect({
        value: operator,
        onChange: onChangeOperator,
        onFocusNext: () => {
            if (!value || value.length === 0) {
                restProps.onOpen?.();
            }
        },
        autoOpen: false,
    });

    // Пользователь ничего не выбрал - удаляем фильтр
    if (!restProps.isOpen && (!value || value.length === 0) && !operatorProps.isOpen && !isRequired) {
        onRemove?.();
    }

    const canRemoveValue = !isRequired || value.length > 1;

    return (
        <FilterItem
            color={color}
            size={'xs'}
            data-testid={dataTestId}
            keyChip={
                // Select
                <ErrorBoundary errorFallback={errorFallback}>
                    <Suspense fallback={fallback}>
                        <DataProviderComponent queryArg={queryArg} children={selectRenderer} />
                    </Suspense>
                </ErrorBoundary>
            }
            isDisabled={isDisabled}
            onRemove={isRequired ? clearFilter : onRemove}
            logNode="FilterItem"
        >
            {/* TODO DIRECT-282638 Отрефакторить это место */}
            <Suspense>
                <AsyncTitleByValue
                    itemTitles={itemTitles}
                    value={value}
                    setItemTitles={setItemTitles}
                    shownValuesLimit={shownValuesLimit}
                    useTitleByValue={useTitleByValue}
                />
            </Suspense>
            <OperatorSelect
                data-testid={dataTestId}
                operators={operators}
                operatorTitles={operatorTitles}
                operatorProps={operatorProps}
                isDisabled={isDisabled}
            />
            <ValueChips
                value={value}
                shownValuesLimit={shownValuesLimit}
                itemTitles={itemTitles}
                renderValuesLimitText={renderValuesLimitText}
                onRemove={onRemove}
                onChange={onChange}
                isDisabled={!canRemoveValue || isDisabled}
                data-testid={dataTestId}
            />
        </FilterItem>
    );
});

type TitleByValueProps = Pick<FilterSelectListProps<never>, 'value' | 'shownValuesLimit'> & {
    itemTitles: ItemsTitlesCache;
    setItemTitles: (newValues: SetStateAction<Record<string, ReactElement | string>>) => void;
    useTitleByValue: Required<FilterSelectListProps<never>>['useTitleByValue'];
};

/**
 * Компонент для запроса заголовков значений. Ничего не рендерит, нужен только чтобы обернуть его в Suspense
 */
function AsyncTitleByValue({
    value,
    itemTitles,
    shownValuesLimit,
    useTitleByValue: useAsyncTitleByValue,
    setItemTitles,
}: TitleByValueProps) {
    // id элементов без имен в кэше
    const idsWithoutName = useMemo(() => {
        return (value && difference(value.slice(0, shownValuesLimit), keys(itemTitles))) ?? [];
    }, [itemTitles, value, shownValuesLimit]);
    const newNamesForIds = useAsyncTitleByValue(idsWithoutName);

    // Добавляем имена элементов в кэш
    useLayoutEffect(() => {
        if (newNamesForIds.length === 0 || idsWithoutName.length === 0) {
            return;
        }

        const titles = newNamesForIds.reduce<Record<string, string>>((acc, item) => {
            acc[item.value] = item.title;
            return acc;
        }, {});
        /** Эту проверку не убирать!
         * useTitleByValue не успевает отрабатывать, возвращает старые значения, но ссылка на них меняется
         * Из-за чего уходим в бесконечную рекурсию  */
        const hasNewTitles = Object.keys(titles).some(key => !itemTitles[key]);
        if (!hasNewTitles) {
            return;
        }

        setItemTitles(prev => ({ ...prev, ...titles }));
    }, [idsWithoutName.length, itemTitles, newNamesForIds, setItemTitles]);

    return null;
}

/**
 * Чипсы с выбранными значениями
 */
function ValueChips({
    value = [],
    shownValuesLimit,
    itemTitles,
    renderValuesLimitText,
    'data-testid': dataTestId = '',
    onRemove,
    onChange,
    isDisabled,
}: Pick<
    FilterSelectListProps,
    'value' | 'shownValuesLimit' | 'renderValuesLimitText' | 'onRemove' | 'onChange' | 'data-testid' | 'isDisabled'
> & { itemTitles: ItemsTitlesCache }) {
    const isValueCountLimited = shownValuesLimit && shownValuesLimit > 0 && value.length > shownValuesLimit;
    const limitValue = isValueCountLimited ? value.slice(0, shownValuesLimit) : [];
    const restCount = isValueCountLimited ? value.length - shownValuesLimit : 0;

    // Коллапс значений если их больше shownValuesLimit
    if (isValueCountLimited) {
        return (
            <>
                {limitValue.map(valueItem => {
                    const content = itemTitles[valueItem];
                    return (
                        <FilterChip key={valueItem} logNode="FilterChip">
                            {content ?? valueItem}
                        </FilterChip>
                    );
                })}

                <FilterChip key={'counter'} logNode="FilterChip" data-testid={`${dataTestId}.counter`}>
                    {renderValuesLimitText
                        ? renderValuesLimitText(restCount)
                        : i18n('и ещё {count}', {
                              count: restCount,
                          })}
                </FilterChip>
            </>
        );
    }

    return (
        <>
            {value.map(valueItem => {
                const content = itemTitles[valueItem];

                if (value.length > 1 || !onRemove) {
                    return (
                        <FilterValueChip
                            key={valueItem}
                            onChange={isDisabled ? undefined : onChange}
                            value={valueItem}
                            values={value}
                            text={content ?? valueItem}
                            logNode="FilterValueChip"
                        />
                    );
                }

                return (
                    <FilterChip key={valueItem} logNode="FilterChip">
                        {content ?? valueItem}
                    </FilterChip>
                );
            })}
        </>
    );
}

function OperatorSelect(props: {
    operatorProps: ReturnType<typeof useFilterExpressionSelect<OperatorType>>;
    'data-testid': string;
    operators?: OperatorType[];
    operatorTitles?: Partial<Record<OperatorType, string>>;
    isDisabled?: boolean;
}) {
    const { operators, operatorTitles, operatorProps, isDisabled, 'data-testid': dataTestId = '' } = props;
    const withOperators = Boolean(operators?.length);

    const operatorsItems = useMemo(() => {
        return (
            operators?.map(operator => ({
                value: operator,
                content: operatorTitles?.[operator] ?? getOperatorTitle(operator),
            })) || []
        );
    }, [operatorTitles, operators]);

    const debouncedOperatorSelectClose = useDebounce(operatorProps.onClose, 0);

    if (!withOperators) {
        return null;
    }

    return (
        <FilterExpressionSelect<OperatorType>
            {...operatorProps}
            isOpen={!operatorProps.value || operatorProps.isOpen}
            data-testid={`${dataTestId}.operatorSelect`}
            placeholder={'-'}
            items={operatorsItems}
            logNode="InputFilterOperatorSelector"
            onClose={debouncedOperatorSelectClose}
            className={styles.operatorSelect}
            isDisabled={isDisabled}
        />
    );
}

/**
 * Фабрика для создания селекта
 */
function useSelectFactory<RowData extends BaseRowData>(
    props: FilterSelectListProps<RowData> & {
        setItemTitles: (newValues: SetStateAction<Record<string, ReactElement | string>>) => void;
        itemTitles: Record<string, ReactElement | string>;
        search: string;
        setSearch: (search: string) => void;
    }
) {
    const [showSelected, setShowSelected] = useState(false);
    const {
        className,
        'data-testid': dataTestId = 'FilterSelectListWithSuggest',
        color = 'normal',
        onChange,
        renderFilterChipsItem,
        renderSelectListItem,
        showSelectedOnlySwitcher,
        setItemTitles,
        search,
        setSearch,
        itemTitles,
        value,
        errors,
        isRequired,
        addonLeft,
        ...restProps
    } = props;

    /**
     * Обработка изменений из селекта
     */
    const onHandleChange = useCallback(
        (keys: string[], items: (RowData | undefined)[]) => {
            // Селект присылает список отображенных элементов, кладем их в кэш имен
            setItemTitles(prevState => mapItemTitles(items, search, renderFilterChipsItem, prevState));
            onChange(keys);
        },
        [setItemTitles, onChange, search, renderFilterChipsItem]
    );

    const renderTrigger = useRenderTrigger({ dataTestId, errors, addonLeft });

    // У селекта не работает батчинг (DIRECT-261664).
    // Откладываем закрытия селекта на один рендер, пока не сработает открытие другого селекта.
    // Это нужно только чтобы удалять пустой фильтр, но позволять переключаться между селектами
    const debouncedMainSelectClose = useDebounce(restProps.onClose ?? noop, 0);

    return useCallback(
        <SelectRowData extends RowData>(
            dataProvider: DataProvider<SelectRowData>,
            { retry, allIds, error }: { retry?(): void; allIds?: string[]; error?: boolean } = {}
        ) => {
            return (
                <Select<string, string, SelectRowData>
                    {...restProps}
                    className={[className, styles.select].join(' ')}
                    color={color}
                    renderTrigger={renderTrigger}
                    renderTriggerContent={renderTriggerContent}
                    data-testid={`${dataTestId}.Select`}
                    onChange={onHandleChange}
                    value={value}
                    onClose={debouncedMainSelectClose}
                    dataProvider={dataProvider}
                    size="s"
                    maxHeight={425}
                    popupWidth={350}
                    disabledKeys={[]}
                    pinnedSection={
                        <>
                            <ListSection>
                                <ListInputItem
                                    value={search}
                                    onChange={setSearch}
                                    placeholder={i18n('Поиск')}
                                    data-testid={`${dataTestId}.Input`}
                                />
                            </ListSection>
                            {showSelectedOnlySwitcher && (
                                <ShowSelectedOnlySwitcher
                                    data-testid={`${dataTestId}.ShowSelectedSwitcher`}
                                    value={String(showSelected)}
                                    onChange={setShowSelected}
                                    selectedCount={value.length}
                                    size="s"
                                />
                            )}
                            {allIds && (
                                <AsyncSelectAllSection
                                    dataProvider={dataProvider}
                                    value={value}
                                    isRequired={isRequired}
                                    allIds={allIds}
                                    itemTitles={itemTitles}
                                    onChange={onChange}
                                    data-testid={dataTestId}
                                />
                            )}
                        </>
                    }
                    emptySection={
                        error ? (
                            <ListItem
                                logNode="Error"
                                // DIRECT-337096: элемент исключён из поиска, при доработке заменить на текст для поиска
                                textValue={null}
                            >
                                <VStack
                                    className={styles.errorBlock}
                                    justifyContent="center"
                                    alignItems="center"
                                    gap="8"
                                >
                                    <Text>{i18n('Произошла ошибка')}</Text>
                                    <Button
                                        color="contour"
                                        size="s"
                                        data-testid={`${dataTestId}.Retry`}
                                        onClick={retry}
                                        logNode="retryButton"
                                        iconLeft={IconRefresh}
                                    >
                                        {i18n('Попробовать снова')}
                                    </Button>
                                </VStack>
                            </ListItem>
                        ) : (
                            <>
                                {showSelected && !value.length ? (
                                    <ListItem
                                        caption={i18n(
                                            'Выберите хотя бы одно значение, чтобы увидеть его в списке выбранных.'
                                        )}
                                    >
                                        {i18n('Нет выбранных значений')}
                                    </ListItem>
                                ) : (
                                    <ListItem data-testid={`${dataTestId}.NoItem`} logNode="NichegoNeNajdeno">
                                        {i18n('Ничего не найдено')}
                                    </ListItem>
                                )}
                            </>
                        )
                    }
                >
                    {(itemState, props) => {
                        const data = itemState.isError ? undefined : itemState.current();

                        return (
                            <ListItem
                                data-testid={`${dataTestId}.${data?.id || ''}`}
                                {...props}
                                // DIRECT-337096: элемент исключён из поиска, при доработке заменить на текст для поиска
                                textValue={null}
                            >
                                {data && (showSelected ? value.includes(data.id) : true)
                                    ? renderSelectListItem({ item: data, props, search })
                                    : null}
                            </ListItem>
                        );
                    }}
                </Select>
            );
        },
        [
            className,
            color,
            dataTestId,
            debouncedMainSelectClose,
            isRequired,
            itemTitles,
            onChange,
            onHandleChange,
            renderSelectListItem,
            renderTrigger,
            restProps,
            search,
            setSearch,
            showSelected,
            showSelectedOnlySwitcher,
            value,
        ]
    );
}

/**
 * Секция с кнопками "Выбрать все" и "Сбросить все"
 */
type SelectAllSectionProps = Required<TestProps> & {
    allIds: string[];
    itemTitles: Record<string, ReactElement | string>;
    isLoading: boolean;
    value: string[];
    onChange: (value: string[]) => void;
    isRequired?: boolean;
};

type AsyncSelectAllSectionProps<RowData extends BaseRowData> = Omit<SelectAllSectionProps, 'isLoading'> & {
    /** Провайдер, по которому определяется состояние загрузки. */
    dataProvider: DataProvider<RowData>;
};

function AsyncSelectAllSection<RowData extends BaseRowData>({
    dataProvider,
    ...props
}: AsyncSelectAllSectionProps<RowData>) {
    const totalCountResult = useAsyncFlow(dataProvider.totalCount);
    const isLoading = totalCountResult.isLoading || totalCountResult.isStale;

    return <SelectAllSection {...props} isLoading={isLoading} />;
}

function SelectAllSection({
    allIds,
    'data-testid': dataTestId,
    isLoading,
    itemTitles,
    value,
    onChange,
    isRequired,
}: SelectAllSectionProps) {
    const unselectedIdsCount = difference(allIds || [], value || []).length;

    const handleResetAll = useCallback(() => {
        if (!isRequired) {
            onChange([]);
        }
    }, [isRequired, onChange]);

    const selectAll = useCallback(() => {
        if (!allIds) {
            return;
        }

        const existingIds = allIds.filter(id => itemTitles[id]);
        const remainingIds = allIds.filter(id => !itemTitles[id]);

        const selectedIds = [...existingIds, ...remainingIds];

        onChange(selectedIds);
    }, [itemTitles, onChange, allIds]);

    return (
        <ListSection>
            {Boolean(allIds.length) && (
                <ListItem
                    data-testid={`${dataTestId}.ChooseAll`}
                    logNode="ChooseAll"
                    onClick={selectAll}
                    isDisabled={!allIds.length || !unselectedIdsCount || isLoading}
                >
                    {isLoading
                        ? i18n('Выбрать все')
                        : i18n('Выбрать все ({count})', {
                              count: unselectedIdsCount,
                          })}
                </ListItem>
            )}
            <ListItem
                data-testid={`${dataTestId}.ResetAll`}
                logNode="ResetAll"
                onClick={handleResetAll}
                isDisabled={!value.length}
            >
                {i18n('Сбросить все')}
            </ListItem>
        </ListSection>
    );
}

const renderTriggerContent = (rowData: BaseRowData) => String(rowData.id);

/**
 * Триггер селекта фильтра
 * @param dataTestId
 */
function useRenderTrigger({
    dataTestId,
    errors,
    addonLeft,
}: {
    dataTestId: string;
    errors?: string[];
    addonLeft?: React.ReactNode;
}) {
    return useMemo(
        () =>
            forwardRef<HTMLElement, SelectTriggerProps<string>>(function RenderTrigger(triggerProps, forwardedRef) {
                const { isOpened, placeholder } = triggerProps;

                return (
                    <FilterChip
                        {...triggerProps}
                        ref={castRef(forwardedRef, 'div')}
                        data-testid={`${dataTestId}.Select`}
                        error={errors?.join('. ')}
                        addonLeft={addonLeft}
                        addonRight={
                            isOpened ? (
                                <IconArrowUp color={'gray'} size={'16'} />
                            ) : (
                                <IconArrowDown color={'gray'} size={'16'} />
                            )
                        }
                    >
                        {placeholder}
                    </FilterChip>
                );
            }),
        [dataTestId, errors, addonLeft]
    );
}
